const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  if (!rootEl) return;

  const animationTriggerElements = [
    ...(rootEl.classList?.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) ? [rootEl] : []),
    ...Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)),
  ];
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => {
    if (element.dataset.scrollAnimationInitialized === 'true') return;
    element.dataset.scrollAnimationInitialized = 'true';
    observer.observe(element);
  });
}

// Zoom in animation logic
const scrollZoomElements = new Set();
let scrollZoomObserver;
let scrollZoomHandlerBound = false;

const updateScrollZoomElements = throttle(() => {
  scrollZoomElements.forEach((element) => {
    if (!element.isConnected) {
      scrollZoomObserver?.unobserve(element);
      scrollZoomElements.delete(element);
      return;
    }

    if (element.dataset.scrollZoomVisible !== 'true') return;
    element.style.setProperty('--zoom-in-ratio', 1 + (0.2 / 100) * percentageSeen(element));
  });
});

function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!rootEl) return;

  const animationTriggerElements = [
    ...(rootEl.classList?.contains(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME) ? [rootEl] : []),
    ...Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)),
  ];

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  scrollZoomObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      entry.target.dataset.scrollZoomVisible = entry.isIntersecting ? 'true' : 'false';
    });
  });

  animationTriggerElements.forEach((element) => {
    if (element.dataset.scrollZoomInitialized === 'true') return;
    element.dataset.scrollZoomInitialized = 'true';
    scrollZoomElements.add(element);
    scrollZoomObserver.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
  });

  if (!scrollZoomHandlerBound) {
    window.addEventListener('scroll', updateScrollZoomElements, { passive: true });
    scrollZoomHandlerBound = true;
  }
}

function unloadScrollZoomAnimationTrigger(rootEl) {
  if (!rootEl || !scrollZoomObserver) return;

  const animationTriggerElements = [
    ...(rootEl.classList?.contains(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME) ? [rootEl] : []),
    ...Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)),
  ];

  animationTriggerElements.forEach((element) => {
    scrollZoomObserver.unobserve(element);
    scrollZoomElements.delete(element);
    delete element.dataset.scrollZoomInitialized;
    delete element.dataset.scrollZoomVisible;
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => unloadScrollZoomAnimationTrigger(event.target));
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger(document);
  });
}
